// ── CAPR S2 — the context an assessment is made and reviewed in ───────────────────────────────
//
// Shared by the assessment screen, the approver view and the history, so the prior decision, the
// control definition and an evidence basis read the same everywhere they appear.

import { CTS_REASON_STARTED_LABEL, type CtsReasonStarted } from '@grc/shared';
import type { ControlHub, HubDecision, HubEvidenceVerification } from '@/api/types/cts-workspace.types';
import type { EvidenceArtifact } from '@/api/types/evidence-artifacts.types';
import type { CtsProposedEvidenceItem } from '@/api/types/cts-tasks.types';
import { ArtifactRow } from '@/components/compliance/CapturedEvidencePanel';
import { EvidenceVerdict } from './EvidenceVerdict';
import {
  ADVERSE_CHANGE_WARNING, AT_SUBMIT_LABEL, NO_EVIDENCE_EXPLAINER, NO_EVIDENCE_LABEL, NO_PRIOR_ASSESSMENT,
  PRIOR_BASIS_MARKER, UNVERIFIED_EXPLAINER, VERIFICATION_NOW_NOTE, VERIFIED_EXPLAINER, adverseChangeText, statusLabel,
} from './assessment-copy';

const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';
const BOX = 'rounded-[8px] border border-border-subtle bg-bg-surface-2 px-[12px] py-[10px]';

export const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

export const reasonLabel = (r: CtsReasonStarted | null): string | null => (r ? CTS_REASON_STARTED_LABEL[r] : null);

/** The latest APPROVED decision, or null. ⓘ Decisions arrive newest first. */
export const latestApproval = (hub: ControlHub): HubDecision | null =>
  hub.decisions.find((d) => d.outcome_kind === 'approved') ?? null;

/** The prior decision — what this assessment either reconfirms or changes (UX-05). */
export function PriorDecisionPanel({ hub }: { hub: ControlHub }) {
  const last = latestApproval(hub);
  return (
    <div className={BOX} data-testid="prior-decision">
      <span className={EYEBROW}>Prior decision</span>
      {last ? (
        <>
          <p className="mt-[4px] text-[13px] text-text-primary">
            {statusLabel(last.implementation_status)}
            <span className="text-[11.5px] text-text-secondary">
              {' · approved '}{fmtDate(last.approved_at)}{last.approver_name ? ` by ${last.approver_name}` : ''}
            </span>
          </p>
          {last.rationale && <p className="mt-[3px] text-[12px] text-text-secondary">{last.rationale}</p>}
        </>
      ) : (
        <p className="mt-[4px] text-[12.5px] italic text-text-faint">{NO_PRIOR_ASSESSMENT}</p>
      )}
    </div>
  );
}

/** The control's CURRENT definition — what is being assessed now (the snapshot is taken at approval). */
export function ControlDefinition({ hub }: { hub: ControlHub }) {
  return (
    <div className={BOX} data-testid="control-definition">
      <span className={EYEBROW}>Control being assessed</span>
      <p className="mt-[4px] text-[13px] text-text-primary">
        <span className="font-mono text-text-secondary">{hub.control.display_code}</span> {hub.control.title}
      </p>
      {hub.control.description && (
        <p className="mt-[3px] text-[12px] leading-[1.5] text-text-secondary">{hub.control.description}</p>
      )}
    </div>
  );
}

/**
 * The evidence a PROPOSAL relies on, read-only, for the approver. Each item is shown with its CURRENT
 * flags (the shared row), the server's verdict NOW (the same map and component as the assessor's
 * picker), what the server recorded when the assessor submitted, and the assessor's note.
 * ⚠ CAPR S4 — no verdict on its own blocks an approval. What does is an ADVERSE CHANGE SINCE SUBMIT
 *   (the server's `proposal` list): it is named here first, with the way forward (return the work).
 */
export function ProposedEvidence({ selection, artifacts, priorIds, verification }: {
  selection: CtsProposedEvidenceItem[]; artifacts: EvidenceArtifact[]; priorIds: Set<string>;
  verification: HubEvidenceVerification;
}) {
  if (selection.length === 0) {
    return (
      <div className={BOX} data-testid="proposed-no-evidence">
        <p className="text-[12.5px] text-text-primary">{NO_EVIDENCE_LABEL}</p>
        <p className="mt-[3px] text-[11.5px] text-text-secondary">{NO_EVIDENCE_EXPLAINER}</p>
      </div>
    );
  }
  const byId = new Map(artifacts.map((a) => [a.artifact_id, a]));
  const adverse = new Map((verification.proposal ?? []).map((p) => [p.artifactId, p.adverseChanges]));
  return (
    <div className="rounded-[8px] border border-border-subtle" data-testid="proposed-evidence">
      <p className="border-b border-border-subtle px-[16px] py-[8px] text-[11.5px] text-text-secondary">
        {VERIFICATION_NOW_NOTE} {VERIFIED_EXPLAINER} An unverified item: {UNVERIFIED_EXPLAINER}
      </p>
      {selection.map((s) => {
        const a = byId.get(s.artifactId);
        const now = verification.items[s.artifactId] ?? null;
        const changes = adverse.get(s.artifactId) ?? [];
        const note = (
          <p className="mt-[6px] text-[12px] text-text-secondary" data-testid="proposed-evidence-note">
            <span className="text-text-faint">Assessor’s note: </span>{s.acknowledgmentNote}
          </p>
        );
        return a ? (
          <ArtifactRow key={s.artifactId} a={a} marker={priorIds.has(s.artifactId) ? PRIOR_BASIS_MARKER : null}>
            <EvidenceVerdict status={now?.status ?? null} reasons={now?.reasons ?? null} />
            {s.verificationAtSubmit && (
              <div className="mt-[5px]" data-testid="proposed-evidence-at-submit">
                <span className="text-[11px] text-text-faint">{AT_SUBMIT_LABEL}:</span>
                <EvidenceVerdict status={s.verificationAtSubmit.status} reasons={s.verificationAtSubmit.reasons} />
              </div>
            )}
            {changes.length > 0 && (
              <div className="mt-[6px] text-[11.5px] text-cert-warn-fg" role="note" data-testid="proposed-evidence-adverse">
                {ADVERSE_CHANGE_WARNING}
                <ul className="list-disc pl-[16px]">
                  {changes.map((c) => <li key={c} data-change={c}>{adverseChangeText(c)}</li>)}
                </ul>
              </div>
            )}
            {note}
          </ArtifactRow>
        ) : (
          // ⓘ Selected at submit but no longer listed for this control: approving will be refused
          //   (the server re-checks), so the approver sees it here first.
          <div key={s.artifactId} className="border-b border-border-subtle px-[16px] py-[11px] last:border-b-0"
               data-testid="proposed-evidence-missing">
            <p className="text-[12.5px] text-cert-warn-fg">This item is no longer linked to this control.</p>
            {note}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A STORED decision's basis (history): what it relied on, and each item's verdict AS RECORDED AT
 * APPROVAL (CAPR S4) — the status and reasons written into the decision, never re-evaluated now. A
 * decision recorded before S4 has every item unverified and no recorded reasons.
 */
export function DecisionBasis({ decision }: { decision: HubDecision }) {
  const b = decision.evidence_basis;
  if (!b) return null;
  if (b.kind !== 'items') {
    return <p className="mt-[4px] text-[11.5px] text-text-secondary">{NO_EVIDENCE_LABEL}</p>;
  }
  return (
    <ul className="mt-[4px] space-y-[3px]" data-testid="decision-basis">
      {b.items.map((i) => (
        <li key={i.evidence_id} className="text-[11.5px] text-text-secondary">
          <span className="text-text-primary">{i.name ?? 'Unnamed evidence'}</span>
          {i.captured_version ? ` · version ${i.captured_version}` : ''}
          <EvidenceVerdict status={i.verification_status} reasons={i.verification_reasons} />
          {i.acknowledgment_note ? <span className="block text-text-faint">{i.acknowledgment_note}</span> : null}
        </li>
      ))}
    </ul>
  );
}
